/*
 * The backpack — window.SourInventory (assets/inventory.js).
 *
 * RuneScape's inventory, shared by Build a Box (assets/box-builder.js) and
 * the cart drawer (assets/cart-drawer.js): a 4-wide grid of item slots
 * (snippets/inventory-slot.liquid / slotHTML), the pointer state machine
 * (tap · hold · drag), the "Choose option" context menu, the item card, the
 * chat line that prints an Examine, the fly-to-backpack animation and the
 * world-item drag source (a flavor tile lifted off the page and dropped
 * into the backpack).
 *
 * It never touches the cart or the box: every consumer passes an adapter
 * and acts in its callbacks. Both consumers guard with
 * `window.SourInventory &&`, so a missing module leaves plain buttons.
 *
 * Gestures (per grid, delegated):
 *   tap        → adapter.onTap      (the click event; keyboard Enter/Space too)
 *   hold 450ms → adapter.menuItems  (the RS menu; phones); right-click on desktop
 *   move > 8px → reorder drag when adapter.drag === 'reorder' (else the page scrolls)
 * The click that trails a hold or a drag is swallowed at the capture phase,
 * so a consumer's own delegated click handlers never see it.
 *
 * Events on document: inventory:menu {open}, inventory:card {open},
 * inventory:examine {text}, inventory:move {from, to}, inventory:drop {payload}.
 */
(() => {
  if (window.SourInventory) return;

  const HOLD_MS = 450; // a slot: hold for the menu
  const LIFT_MS = 250; // a world item on touch: hold to lift it
  const MOVE_PX = 8;
  const CHAT_MS = 4000;
  const CLICK_GUARD_MS = 400;

  const reduced = () => !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  const finePointer = () => !!(window.matchMedia && window.matchMedia('(pointer: fine)').matches);
  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]));
  const emit = (name, detail) => document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: detail || {} }));
  const vibrate = (ms) => {
    try {
      if (navigator.vibrate) navigator.vibrate(ms);
    } catch (e) {}
  };
  const rectOf = (x) => (x && typeof x.getBoundingClientRect === 'function' ? x.getBoundingClientRect() : x);
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

  /* ---------- markup ---------- */

  /** The sour face for a level (the card and the builder's JS-rendered slots). `base` is the asset URL of sour_1.webp. */
  const faceHTML = (level, base, cls) => {
    const lvl = Number(level) || 0;
    if (!(lvl > 0) || !base) return '';
    const n = Math.max(1, Math.min(5, Math.round(lvl * 0.05)));
    const src = String(base).replace('sour_1.webp', 'sour_' + n + '.webp');
    return '<span class="sour-face sour-face--sm ' + esc(cls || '') + '" aria-hidden="true"><img class="sour-face__icon" src="' + esc(src) + '" width="45" height="45" loading="lazy" alt=""></span>';
  };

  /**
   * One slot, the same markup snippets/inventory-slot.liquid renders.
   * model: {kind, image, title, level, count, examine, url, index, faceBase, label, attrs, min, id, static, removable}
   * `removable: false`: a filled slot without the × (the box line in Your Squire's Box).
   * `static`: a decorative cell (the phone bar's peek strip) — <span> shells, no button,
   * no ARIA; the strip's own button carries the label (a <button> can't hold buttons).
   */
  const slotHTML = (m) => {
    const kind = m.kind || (m.image ? 'bag' : 'empty');
    const filled = !!m.image;
    const cls = ['inv-slot', 'inv-slot--' + kind];
    if (filled) cls.push('is-filled');
    if (m.target) cls.push('inv-slot--target');
    if (m.min) cls.push('inv-slot--min');
    const label = m.label || m.title || '';
    let inner = '';
    if (filled) {
      inner += '<img class="inv-slot__art" src="' + esc(m.image) + '" alt="" width="56" height="56" loading="lazy" draggable="false">';
      if (kind === 'bag') inner += faceHTML(m.level, m.faceBase, 'inv-slot__face');
      if (Number(m.count) > 1) inner += '<span class="inv-slot__count" data-cart-qty-value>' + Number(m.count) + '</span>';
    }
    const open = '<' + (m.static ? 'span' : 'li') + ' class="' + cls.join(' ') + '" data-inv-slot data-index="' + Number(m.index || 0) + '"' + (m.id ? ' data-id="' + esc(m.id) + '"' : '') + (m.attrs ? ' ' + m.attrs : '') + '>';
    if (m.static) return open + '<span class="inv-slot__btn" data-inv-btn>' + inner + '</span></span>';
    return (
      open +
      '<button type="button" class="inv-slot__btn" data-inv-btn aria-label="' + esc(label) + '"' + (filled ? ' aria-haspopup="dialog"' : '') + (m.examine ? ' data-examine="' + esc(m.examine) + '"' : '') + (m.url ? ' data-url="' + esc(m.url) + '"' : '') + '>' +
      inner +
      '</button>' +
      (filled && m.removable !== false ? removeHTML(m.removeLabel || label) : '') +
      '</li>'
    );
  };
  /** The × on a filled slot (a sibling of the button; mount() routes it to onRemove). */
  const removeHTML = (label) => '<button type="button" class="inv-slot__remove" data-inv-remove aria-label="' + esc(label) + '">×</button>';

  /** Cells to draw: at least two rows, the "+" slot after the last item once the minimum is met. */
  const cellCount = (filled, min, cols, minRows) => {
    const c = cols || 4;
    const more = filled >= (min || 0) ? 1 : 0;
    return Math.max((minRows || 2) * c, Math.ceil((filled + more) / c) * c);
  };

  /* ---------- the chat line ---------- */

  const chatTimers = new WeakMap();
  const say = (chatEl, text) => {
    if (!chatEl) return;
    clearTimeout(chatTimers.get(chatEl));
    chatEl.textContent = text || '';
    chatEl.hidden = !text;
    chatEl.classList.remove('is-in');
    void chatEl.offsetWidth;
    chatEl.classList.add('is-in');
    emit('inventory:examine', { text: text });
    chatTimers.set(
      chatEl,
      setTimeout(() => {
        chatEl.classList.remove('is-in');
        setTimeout(() => {
          if (!chatEl.classList.contains('is-in')) chatEl.hidden = true;
        }, reduced() ? 0 : 250);
      }, CHAT_MS)
    );
  };

  /** A chat line with no home: one fixed toast above the page's bottom bar (--builder-sticky-h). */
  let toastEl = null;
  const toast = (text) => {
    if (!toastEl) {
      toastEl = document.createElement('p');
      toastEl.className = 'inv-chat inv-toast';
      toastEl.setAttribute('role', 'status');
      toastEl.setAttribute('aria-live', 'polite');
      toastEl.hidden = true;
      document.body.appendChild(toastEl);
    }
    say(toastEl, text);
  };

  /* ---------- the "Choose option" menu ---------- */

  const menu = {
    el: null,
    anchor: null,
    onClose: null,
    ensure() {
      if (this.el) return this.el;
      const el = document.createElement('div');
      el.className = 'inv-menu';
      el.setAttribute('role', 'menu');
      el.hidden = true;
      el.innerHTML = '<div class="panel-bg flat" aria-hidden="true"></div><p class="inv-menu__title" data-inv-menu-title></p><div class="inv-menu__items" data-inv-menu-items></div>';
      document.body.appendChild(el);
      el.addEventListener('click', (e) => {
        const b = e.target.closest('[data-inv-menu-item]');
        if (!b || b.disabled) return;
        const item = this.items && this.items[Number(b.dataset.invMenuItem)];
        this.close({ ran: true });
        if (item && typeof item.run === 'function') item.run();
      });
      el.addEventListener('keydown', (e) => {
        const buttons = [...el.querySelectorAll('[data-inv-menu-item]:not([disabled])')];
        const i = buttons.indexOf(document.activeElement);
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          e.preventDefault();
          if (!buttons.length) return;
          const next = e.key === 'ArrowDown' ? (i + 1) % buttons.length : (i - 1 + buttons.length) % buttons.length;
          buttons[next].focus();
        } else if (e.key === 'Escape' || e.key === 'Tab') {
          e.preventDefault();
          this.close({ focus: true });
        }
      });
      this.el = el;
      return el;
    },
    get isOpen() {
      return !!this.el && !this.el.hidden;
    },
    /** items: [{label, run, disabled, key}]; anchor: an element or {x, y}. */
    open(opts) {
      const o = opts || {};
      const el = this.ensure();
      if (this.isOpen) this.close();
      const items = (o.items || []).filter(Boolean);
      if (!items.length) return;
      this.items = items;
      // `anchor` places the menu (an element, or the pointer's {x, y}); focus returns to `returnTo` (else the anchor element).
      this.anchor = o.returnTo || (o.anchor && o.anchor.nodeType ? o.anchor : null);
      this.onClose = o.onClose || null;
      el.querySelector('[data-inv-menu-title]').textContent = o.title || '';
      el.querySelector('[data-inv-menu-items]').innerHTML = items
        .map((it, i) => '<button type="button" role="menuitem" class="inv-menu__item' + (it.primary ? ' is-primary' : '') + '" data-inv-menu-item="' + i + '"' + (it.disabled ? ' disabled' : '') + '>' + esc(it.label) + '</button>')
        .join('');
      el.hidden = false;
      el.setAttribute('aria-label', o.title || '');
      // Position: below the anchor's left edge (or the pointer), clamped to the viewport; flips above when it would overflow.
      const at = o.anchor && o.anchor.nodeType ? o.anchor.getBoundingClientRect() : { left: o.anchor ? o.anchor.x : 0, top: o.anchor ? o.anchor.y : 0, bottom: o.anchor ? o.anchor.y : 0, width: 0 };
      const r = at;
      const w = el.offsetWidth;
      const h = el.offsetHeight;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      let left = Math.max(8, Math.min(vw - w - 8, r.left));
      let top = r.bottom + 4;
      if (top + h > vh - 8) top = Math.max(8, r.top - h - 4);
      el.style.left = left + 'px';
      el.style.top = top + 'px';
      el.classList.remove('is-in');
      void el.offsetWidth;
      el.classList.add('is-in');
      const first = el.querySelector('[data-inv-menu-item]:not([disabled])');
      if (first) first.focus({ preventScroll: true });
      this.bindDismiss();
      emit('inventory:menu', { open: true });
    },
    bindDismiss() {
      if (this.dismiss) return;
      const off = (e) => {
        if (e.type === 'pointerdown' && this.el && this.el.contains(e.target)) return;
        this.close();
      };
      this.dismiss = off;
      document.addEventListener('pointerdown', off, true);
      document.addEventListener('scroll', off, { capture: true, passive: true });
      window.addEventListener('resize', off);
      document.addEventListener('cart:updated', off);
    },
    close(opts) {
      if (!this.isOpen) return;
      const o = opts || {};
      this.el.hidden = true;
      this.el.classList.remove('is-in');
      if (this.dismiss) {
        document.removeEventListener('pointerdown', this.dismiss, true);
        document.removeEventListener('scroll', this.dismiss, { capture: true });
        window.removeEventListener('resize', this.dismiss);
        document.removeEventListener('cart:updated', this.dismiss);
        this.dismiss = null;
      }
      const anchor = this.anchor;
      this.anchor = null;
      this.items = null;
      if (o.focus && anchor && typeof anchor.focus === 'function') {
        try {
          anchor.focus({ preventScroll: true });
        } catch (e) {}
      }
      const cb = this.onClose;
      this.onClose = null;
      if (cb) cb(o);
      emit('inventory:menu', { open: false });
    },
  };

  /* ---------- the item card ---------- */

  const card = {
    el: null,
    opener: null,
    model: null,
    ensure() {
      if (this.el) return this.el;
      const el = document.createElement('div');
      el.className = 'inv-card';
      el.hidden = true;
      el.innerHTML =
        '<div class="inv-card__scrim" data-inv-card-close></div>' +
        '<div class="inv-card__panel" role="dialog" aria-modal="true" aria-labelledby="inv-card-title" tabindex="-1">' +
        '<div class="panel-bg" aria-hidden="true"></div>' +
        '<button type="button" class="inv-card__close" data-inv-card-close aria-label="Close"></button>' +
        '<div class="inv-card__media"><img class="inv-card__art" alt="" draggable="false" data-inv-card-art></div>' +
        '<div class="inv-card__body" data-inv-card-body></div>' +
        '</div>';
      document.body.appendChild(el);
      el.addEventListener('click', (e) => {
        if (e.target.closest('[data-inv-card-close]')) {
          this.close({ focus: true });
          return;
        }
        const b = e.target.closest('[data-inv-card-action]');
        if (!b || b.disabled) return;
        const action = this.model && this.model.actions && this.model.actions[Number(b.dataset.invCardAction)];
        if (!action) return;
        if (action.href) return; // a link: let it navigate
        e.preventDefault();
        if (action.keep !== true) this.close();
        if (typeof action.run === 'function') action.run();
      });
      el.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          this.close({ focus: true });
        } else if (e.key === 'Tab') {
          const items = [...el.querySelectorAll(FOCUSABLE)].filter((n) => n.offsetParent !== null);
          if (!items.length) return;
          const first = items[0];
          const last = items[items.length - 1];
          if (e.shiftKey && document.activeElement === first) {
            e.preventDefault();
            last.focus();
          } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      });
      this.el = el;
      return el;
    },
    get isOpen() {
      return !!this.el && !this.el.hidden;
    },
    /**
     * model: {image, title, pill, pillClass, price, priceNote, level, zoneName, zoneColor,
     *         faceBase, chips: [], examine, note, actions: [{label, run, href, disabled, primary, keep}],
     *         closeLabel, opener, onClose}
     */
    open(model) {
      const m = model || {};
      const el = this.ensure();
      if (menu.isOpen) menu.close();
      this.model = m;
      this.opener = m.opener || document.activeElement;
      this.onClose = m.onClose || null;
      // The item's kind on the card, so a packet's card can be purple (inventory.css).
      el.dataset.kind = m.kind || '';
      const art = el.querySelector('[data-inv-card-art]');
      art.src = m.image || '';
      art.hidden = !m.image;
      el.querySelector('.inv-card__close').setAttribute('aria-label', m.closeLabel || 'Close');
      let html = '';
      if (m.pill) html += '<span class="sb-pill inv-card__pill ' + esc(m.pillClass || '') + '">' + esc(m.pill) + '</span>';
      html += '<h3 class="inv-card__title" id="inv-card-title">' + esc(m.title || '') + '</h3>';
      if (m.price || m.priceHTML || m.priceNote) html += '<p class="inv-card__price">' + (m.priceHTML || esc(m.price || '')) + (m.priceNote ? ' <span class="inv-card__price-note">' + esc(m.priceNote) + '</span>' : '') + '</p>';
      if (Number(m.level) > 0) {
        html += '<p class="inv-card__level"' + (m.zoneColor ? ' style="--zone: ' + esc(m.zoneColor) + '"' : '') + '>' + faceHTML(m.level, m.faceBase, 'inv-card__face') + '<span>' + esc(m.levelText || '') + (m.zoneName ? ' <b>' + esc(m.zoneName) + '</b>' : '') + '</span></p>';
      }
      if (m.chips && m.chips.length) html += '<ul class="inv-card__chips">' + m.chips.map((c) => '<li>' + esc(c) + '</li>').join('') + '</ul>';
      if (m.examine) html += '<p class="inv-card__examine">' + esc(m.examine) + '</p>';
      if (m.note) html += '<p class="inv-card__note">' + esc(m.note) + '</p>';
      const actions = (m.actions || []).filter(Boolean);
      if (actions.length) {
        html +=
          '<div class="inv-card__actions">' +
          actions
            .map((a, i) => {
              const cls = 'decorated-button-alt inv-card__btn' + (a.primary ? ' is-primary' : '') + (a.quiet ? ' is-quiet' : '');
              if (a.href) return '<a class="' + cls + '" href="' + esc(a.href) + '" data-inv-card-action="' + i + '">' + esc(a.label) + '<span class="decorated-button-alt__sparks"></span></a>';
              return '<button type="button" class="' + cls + '" data-inv-card-action="' + i + '"' + (a.disabled ? ' disabled' : '') + '>' + esc(a.label) + '<span class="decorated-button-alt__sparks"></span></button>';
            })
            .join('') +
          '</div>';
      }
      el.querySelector('[data-inv-card-body]').innerHTML = html;
      el.hidden = false;
      void el.offsetWidth;
      el.classList.add('is-open');
      const panel = el.querySelector('.inv-card__panel');
      panel.focus({ preventScroll: true });
      emit('inventory:card', { open: true });
    },
    close(opts) {
      if (!this.isOpen) return;
      const o = opts || {};
      const el = this.el;
      el.classList.remove('is-open');
      const finish = () => {
        if (!el.classList.contains('is-open')) el.hidden = true;
      };
      setTimeout(finish, reduced() ? 0 : 220);
      const opener = this.opener;
      this.opener = null;
      this.model = null;
      if (o.focus && opener && typeof opener.focus === 'function' && document.contains(opener)) {
        try {
          opener.focus({ preventScroll: true });
        } catch (e) {}
      }
      const cb = this.onClose;
      this.onClose = null;
      if (cb) cb();
      emit('inventory:card', { open: false });
    },
  };

  /* ---------- fly to the backpack ---------- */

  /** One image flies from `from` (element or rect) to `to` (element or rect). Resolves when it lands. */
  const fly = (opts) => {
    const o = opts || {};
    const from = rectOf(o.from);
    const to = rectOf(o.to);
    if (!from || !to || !o.src || reduced()) return Promise.resolve();
    return new Promise((resolve) => {
      const size = o.size || 44;
      const img = document.createElement('img');
      img.className = 'inv-fly';
      img.src = o.src;
      img.alt = '';
      img.style.width = size + 'px';
      img.style.height = size + 'px';
      const sx = from.left + from.width / 2 - size / 2;
      const sy = from.top + from.height / 2 - size / 2;
      img.style.transform = 'translate(' + sx + 'px, ' + sy + 'px) scale(' + (o.fromScale || 1.4) + ')';
      document.body.appendChild(img);
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        img.remove();
        resolve();
      };
      img.addEventListener('transitionend', finish);
      setTimeout(finish, 800);
      void img.offsetWidth;
      const ex = to.left + to.width / 2 - size / 2;
      const ey = to.top + to.height / 2 - size / 2;
      img.classList.add('is-flying');
      img.style.transform = 'translate(' + ex + 'px, ' + ey + 'px) scale(0.35)';
      img.style.opacity = '0.7';
    });
  };

  /* ---------- the ghost ---------- */

  const ghost = {
    el: null,
    show(src, x, y, size) {
      this.hide();
      const g = document.createElement('img');
      g.className = 'inv-ghost';
      g.src = src;
      g.alt = '';
      g.draggable = false;
      g.style.width = (size || 72) + 'px';
      g.style.height = (size || 72) + 'px';
      document.body.appendChild(g);
      this.el = g;
      this.size = size || 72;
      this.move(x, y);
      void g.offsetWidth;
      g.classList.add('is-lifted');
    },
    move(x, y) {
      if (!this.el) return;
      this.el.style.transform = 'translate(' + (x - this.size / 2) + 'px, ' + (y - this.size / 2) + 'px)';
    },
    snapBack(rect) {
      const g = this.el;
      if (!g) return;
      this.el = null;
      if (!rect || reduced()) {
        g.remove();
        return;
      }
      g.classList.add('is-returning');
      g.style.transform = 'translate(' + (rect.left + rect.width / 2 - this.size / 2) + 'px, ' + (rect.top + rect.height / 2 - this.size / 2) + 'px) scale(0.6)';
      g.style.opacity = '0';
      setTimeout(() => g.remove(), 260);
    },
    hide() {
      if (this.el) this.el.remove();
      this.el = null;
    },
  };

  /* ---------- the pointer state machine ---------- */

  /**
   * gesture(el, h): delegated on `el` for targets matching h.match.
   *   h.hold(target, e) → ms before onHold fires (null: never)
   *   h.onHold(target, e, st) → 'drag' to continue as a drag from the hold, else the gesture is over
   *   h.canDrag(target, st) → a move past MOVE_PX starts a drag
   *   h.onDragStart(target, e, st) / onDragMove(e, st) / onDragEnd(e, st, cancelled)
   *   h.onTap(target, e) — the click event (keyboard too), unless a hold or drag just ran
   *   h.onContext(target, e) — right-click / the context-menu key
   *   h.onHoldRelease(target, e, st) — pointerup after a hold that did not become a drag
   */
  const gesture = (el, h) => {
    let st = null;
    let guardUntil = 0;
    const guard = () => (guardUntil = Date.now() + CLICK_GUARD_MS);
    const clear = () => {
      if (st) clearTimeout(st.timer);
      st = null;
    };
    const down = (e) => {
      if (e.button != null && e.button !== 0) return;
      const t = e.target.closest(h.match);
      if (!t || !el.contains(t)) return;
      if (st) clear();
      st = { target: t, x: e.clientX, y: e.clientY, id: e.pointerId, type: e.pointerType, moved: false, dragging: false, held: false, timer: null };
      try {
        t.setPointerCapture(e.pointerId);
      } catch (err) {}
      const hold = h.hold ? h.hold(t, e, st) : null;
      if (hold != null && hold >= 0) {
        st.timer = setTimeout(() => {
          if (!st || st.moved) return;
          st.held = true;
          guard();
          const r = h.onHold ? h.onHold(st.target, e, st) : null;
          if (r === 'drag') {
            st.dragging = true;
            if (h.onDragStart) h.onDragStart(st.target, e, st);
          }
        }, hold);
      }
    };
    const move = (e) => {
      if (!st || e.pointerId !== st.id) return;
      if (st.dragging) {
        e.preventDefault();
        if (h.onDragMove) h.onDragMove(e, st);
        return;
      }
      if (st.held) return;
      if (st.moved) return;
      const dx = e.clientX - st.x;
      const dy = e.clientY - st.y;
      if (dx * dx + dy * dy < MOVE_PX * MOVE_PX) return;
      st.moved = true;
      clearTimeout(st.timer);
      if (h.canDrag && h.canDrag(st.target, st)) {
        st.dragging = true;
        guard();
        if (h.onDragStart) h.onDragStart(st.target, e, st);
        if (h.onDragMove) h.onDragMove(e, st);
      } else {
        // The browser scrolls; nothing else fires.
        st = null;
      }
    };
    const up = (e) => {
      if (!st || e.pointerId !== st.id) return;
      const s = st;
      clear();
      if (s.dragging) {
        guard();
        if (h.onDragEnd) h.onDragEnd(e, s, false);
        return;
      }
      if (s.held) {
        guard();
        if (h.onHoldRelease) h.onHoldRelease(s.target, e, s);
      }
      // A plain tap continues as the click event (keyboard-friendly).
    };
    const cancel = (e) => {
      if (!st || (e && e.pointerId != null && e.pointerId !== st.id)) return;
      const s = st;
      clear();
      if (s.dragging) {
        guard();
        if (h.onDragEnd) h.onDragEnd(e, s, true);
      }
    };
    const click = (e) => {
      const t = e.target.closest(h.match);
      if (!t || !el.contains(t)) return;
      if (Date.now() < guardUntil) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        guardUntil = 0;
        return;
      }
      if (h.onTap) h.onTap(t, e);
    };
    const context = (e) => {
      const t = e.target.closest(h.match);
      if (!t || !el.contains(t)) return;
      e.preventDefault();
      if (st) {
        const s = st;
        clear();
        if (s.dragging && h.onDragEnd) h.onDragEnd(e, s, true);
      }
      if (h.onContext) h.onContext(t, e);
    };
    const key = (e) => {
      if (e.key === 'Escape' && st && st.dragging) cancel();
    };
    el.addEventListener('pointerdown', down);
    el.addEventListener('pointermove', move);
    el.addEventListener('pointerup', up);
    el.addEventListener('pointercancel', cancel);
    el.addEventListener('lostpointercapture', (e) => {
      if (st && !st.dragging && !st.held) return; // a capture handoff mid-press: keep the press
      cancel(e);
    });
    el.addEventListener('click', click, true);
    el.addEventListener('contextmenu', context);
    el.addEventListener('dragstart', (e) => e.preventDefault());
    document.addEventListener('keydown', key);
    return {
      destroy() {
        el.removeEventListener('pointerdown', down);
        el.removeEventListener('pointermove', move);
        el.removeEventListener('pointerup', up);
        el.removeEventListener('pointercancel', cancel);
        el.removeEventListener('click', click, true);
        el.removeEventListener('contextmenu', context);
        document.removeEventListener('keydown', key);
      },
      cancel,
    };
  };

  /* ---------- the grid ---------- */

  const hitCell = (grid, x, y) => {
    const under = document.elementFromPoint(x, y);
    const cell = under && under.closest && under.closest('[data-inv-slot]');
    return cell && grid.contains(cell) ? cell : null;
  };
  const slotIndex = (slot) => Number(slot.dataset.index) || 0;

  /**
   * mount(gridEl, adapter): idempotent (re-run after a re-render). adapter:
   *   drag: 'reorder' | 'out' | 'none'          — 'out': a filled slot can be dragged, but only off the grid
   *   onDragOut(index, slotEl)                  — a slot dragged off the grid and let go (reorder or out)
   *   onTap(slotEl, index, btn)                 — default: card(slot) → card.open
   *   card(slotEl, index) → card model          — the item card (a filled slot)
   *   menuItems(slotEl, index) → items          — the RS menu (hold / right-click); [] = no menu
   *   menuTitle                                  — "Choose option"
   *   onEmptyTap(slotEl, index)                  — a tap on an empty / "+" slot
   *   onMove(from, to)                           — a reorder drop
   *   onDropIn(payload, index)                   — a world item dropped on this grid (see dragSource)
   *   onRemove(slotEl, index)                    — the × on a filled slot
   *   chatEl                                     — where Examine prints
   */
  const mount = (gridEl, adapter) => {
    if (!gridEl) return null;
    if (gridEl.__inv) {
      gridEl.__inv.adapter = adapter || gridEl.__inv.adapter;
      return gridEl.__inv;
    }
    const inv = { el: gridEl, adapter: adapter || {}, drag: null };
    gridEl.__inv = inv;
    const filled = (slot) => slot && slot.classList.contains('is-filled');
    // The × is a sibling of the slot button, outside the gesture's match, so a
    // press on it never lifts or opens the card; a click takes the item out.
    gridEl.addEventListener('click', (e) => {
      const x = e.target.closest('[data-inv-remove]');
      if (!x || !gridEl.contains(x)) return;
      e.preventDefault();
      e.stopPropagation();
      const slot = x.closest('[data-inv-slot]');
      const a = inv.adapter;
      if (!slot || !a.onRemove || !filled(slot)) return;
      vibrate(8);
      a.onRemove(slot, slotIndex(slot));
    });
    const openMenu = (btn, anchor) => {
      const slot = btn.closest('[data-inv-slot]');
      const a = inv.adapter;
      if (!slot || !a.menuItems) return;
      const items = a.menuItems(slot, slotIndex(slot)) || [];
      if (!items.length) return;
      vibrate(10);
      menu.open({ anchor: anchor || btn, returnTo: btn, items: items, title: a.menuTitle || 'Choose option' });
    };
    inv.gesture = gesture(gridEl, {
      match: '[data-inv-btn]',
      hold: (btn) => (filled(btn.closest('[data-inv-slot]')) && inv.adapter.menuItems ? HOLD_MS : null),
      onHold: (btn) => {
        openMenu(btn);
        return null;
      },
      canDrag: (btn) => (inv.adapter.drag === 'reorder' || inv.adapter.drag === 'out') && filled(btn.closest('[data-inv-slot]')),
      onDragStart: (btn, e, st) => {
        const slot = btn.closest('[data-inv-slot]');
        const img = slot.querySelector('.inv-slot__art');
        st.slot = slot;
        st.from = slotIndex(slot);
        st.over = null;
        slot.classList.add('is-lifted');
        gridEl.classList.add('is-dragging');
        if (img) ghost.show(img.currentSrc || img.src, e.clientX, e.clientY, Math.round(slot.getBoundingClientRect().width * 1.1));
        vibrate(8);
      },
      onDragMove: (e, st) => {
        if (st.raf) return;
        st.raf = requestAnimationFrame(() => {
          st.raf = null;
          ghost.move(e.clientX, e.clientY);
          const cell = hitCell(gridEl, e.clientX, e.clientY);
          if (cell !== st.over) {
            if (st.over) st.over.classList.remove('is-over');
            st.over = cell && cell !== st.slot ? cell : null;
            if (st.over) st.over.classList.add('is-over');
          }
        });
      },
      onDragEnd: (e, st, cancelled) => {
        if (st.raf) cancelAnimationFrame(st.raf);
        gridEl.classList.remove('is-dragging');
        if (st.slot) st.slot.classList.remove('is-lifted');
        if (st.over) st.over.classList.remove('is-over');
        const a = inv.adapter;
        const target = !cancelled && e ? hitCell(gridEl, e.clientX, e.clientY) : null;
        // Let go well outside the grid: the item leaves the box (the RS "drop").
        let out = false;
        if (!cancelled && e && !target && a.onDragOut) {
          const r = gridEl.getBoundingClientRect();
          const m = 24;
          out = e.clientX < r.left - m || e.clientX > r.right + m || e.clientY < r.top - m || e.clientY > r.bottom + m;
        }
        if (target && target !== st.slot && a.drag === 'reorder') {
          ghost.hide();
          const to = slotIndex(target);
          if (a.onMove) a.onMove(st.from, to);
          emit('inventory:move', { from: st.from, to: to });
        } else if (out) {
          ghost.hide();
          vibrate(10);
          a.onDragOut(st.from, st.slot);
          emit('inventory:out', { from: st.from });
        } else {
          ghost.snapBack(st.slot ? st.slot.getBoundingClientRect() : null);
        }
      },
      onTap: (btn, e) => {
        const slot = btn.closest('[data-inv-slot]');
        const a = inv.adapter;
        if (!slot) return;
        const i = slotIndex(slot);
        if (!filled(slot)) {
          if (a.onEmptyTap) a.onEmptyTap(slot, i);
          return;
        }
        if (a.onTap) {
          a.onTap(slot, i, btn);
          return;
        }
        if (a.card) {
          const model = a.card(slot, i);
          if (model) {
            model.opener = btn;
            card.open(model);
          }
        }
      },
      onContext: (btn, e) => openMenu(btn, e.clientX != null && e.clientX > 0 ? { x: e.clientX, y: e.clientY } : btn),
    });
    return inv;
  };

  const unmount = (gridEl) => {
    if (!gridEl || !gridEl.__inv) return;
    if (gridEl.__inv.gesture) gridEl.__inv.gesture.destroy();
    delete gridEl.__inv;
  };

  /* ---------- world items: a tile lifted into the backpack ---------- */

  /**
   * dragSource(el, opts): el is the tile; opts:
   *   handle: selector inside el that starts the drag (default: the whole tile)
   *   payload: any (or a function of el)
   *   ghostSrc: image URL (or a function of el)
   *   targets(): [elements] — grids ([data-inv-grid]) and buttons; the one under the pointer lights up
   *   onDrop(payload, targetEl, index|null, e)
   *   onDragState(active)                       — the consumer marks the page (the bar's un-park)
   *   menuItems(el) → items                     — hold without a move (phones) / right-click: the RS menu (Take · Examine)
   *   menuTitle
   */
  const dragSource = (el, opts) => {
    const o = opts || {};
    const val = (v, ...args) => (typeof v === 'function' ? v(...args) : v);
    const targetsNow = () => (o.targets ? o.targets() : []).filter(Boolean);
    const hit = (x, y) => {
      const list = targetsNow();
      for (let i = 0; i < list.length; i++) {
        const t = list[i];
        if (t.hidden || t.offsetParent === null) continue;
        const r = t.getBoundingClientRect();
        if (x >= r.left && x <= r.right && y >= r.top && y <= r.bottom) {
          const cell = t.hasAttribute('data-inv-grid') ? hitCell(t, x, y) : null;
          return { el: t, cell: cell };
        }
      }
      return null;
    };
    let lastOver = null;
    const setOver = (h) => {
      const next = h ? h.cell || h.el : null;
      if (next === lastOver) return;
      if (lastOver) lastOver.classList.remove('is-over');
      if (h && h.cell) h.el.classList.remove('is-over');
      lastOver = next;
      if (lastOver) lastOver.classList.add('is-over');
    };
    const clearOver = () => {
      setOver(null);
      targetsNow().forEach((t) => t.classList.remove('is-over'));
    };
    const start = (e, st) => {
      st.rect = el.getBoundingClientRect();
      el.classList.add('is-lifted');
      document.body.classList.add('inv-dragging');
      ghost.show(val(o.ghostSrc, el) || '', e.clientX, e.clientY, o.ghostSize || 72);
      if (o.onDragState) o.onDragState(true);
      vibrate(8);
    };
    const end = (e, st, cancelled) => {
      if (st.raf) cancelAnimationFrame(st.raf);
      el.classList.remove('is-lifted');
      document.body.classList.remove('inv-dragging');
      const h = !cancelled && e && e.clientX != null ? hit(e.clientX, e.clientY) : null;
      clearOver();
      if (o.onDragState) o.onDragState(false);
      if (h) {
        const payload = val(o.payload, el);
        const index = h.cell ? slotIndex(h.cell) : null;
        ghost.hide();
        if (o.onDrop) o.onDrop(payload, h.el, index, e);
        emit('inventory:drop', { payload: payload, index: index });
      } else {
        ghost.snapBack(st.rect);
      }
    };
    const openMenu = (anchor) => {
      const items = o.menuItems ? o.menuItems(el) || [] : [];
      if (!items.length) return;
      vibrate(10);
      menu.open({ anchor: anchor, returnTo: el.querySelector(o.handle || 'a, button') || el, items: items, title: o.menuTitle || 'Choose option' });
    };
    const liftable = () => (o.canLift ? !!o.canLift(el) : true);
    const g = gesture(el, {
      match: o.handle || '*',
      // Touch: hold to lift (a move before that scrolls the page); an item that
      // cannot be taken (sold out, Squire's only) still holds for its menu.
      // Mouse: no hold, a move lifts.
      hold: (t, e) => (e.pointerType === 'mouse' || finePointer() ? null : LIFT_MS),
      onHold: (t, e, st) => {
        if (!liftable()) {
          openMenu(t);
          return null;
        }
        st.fromHold = true;
        start(e, st);
        return 'drag';
      },
      canDrag: (t, st) => (st.type === 'mouse' || finePointer()) && liftable(),
      onDragStart: (t, e, st) => {
        if (!st.fromHold) start(e, st);
        st.x0 = e.clientX;
        st.y0 = e.clientY;
      },
      onDragMove: (e, st) => {
        st.lastX = e.clientX;
        st.lastY = e.clientY;
        if (Math.abs(e.clientX - st.x) > MOVE_PX || Math.abs(e.clientY - st.y) > MOVE_PX) st.travelled = true;
        if (st.raf) return;
        st.raf = requestAnimationFrame(() => {
          st.raf = null;
          ghost.move(st.lastX, st.lastY);
          setOver(hit(st.lastX, st.lastY));
        });
      },
      onDragEnd: (e, st, cancelled) => {
        // A hold that never moved: the item was only picked up to look at — the RS menu.
        if (st.fromHold && !st.travelled && !cancelled) {
          end(e, st, true);
          openMenu(el.querySelector(o.handle || '*') || el);
          return;
        }
        end(e, st, cancelled);
      },
      onContext: (t, e) => openMenu(e.clientX > 0 ? { x: e.clientX, y: e.clientY } : t),
    });
    el.__invSource = g;
    return g;
  };

  window.SourInventory = {
    HOLD_MS: HOLD_MS,
    slotHTML: slotHTML,
    faceHTML: faceHTML,
    cellCount: cellCount,
    say: say,
    toast: toast,
    menu: menu,
    card: card,
    fly: fly,
    gesture: gesture,
    mount: mount,
    unmount: unmount,
    dragSource: dragSource,
    esc: esc,
  };
})();
